import React from 'react';
import { Card, CardContent, Box, Typography, LinearProgress, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { SxProps } from '@mui/system';

interface ResourceLimitCardProps {
  icon?: React.ReactNode;
  title: string;
  used: number;
  limit?: number | null; // null/undefined => unlimited
  resetAt?: string | Date | null;
  isAdmin?: boolean;
  upgradeHref?: string; // Defaults to /settings/plans when omitted
  thresholdPercent?: number; // Defaults to 90
  sx?: SxProps;
}

const ResourceLimitCard: React.FC<ResourceLimitCardProps> = ({ icon, title, used, limit, resetAt, isAdmin = false, upgradeHref = '/settings/plans', thresholdPercent = 90, sx }) => {
  const isUnlimited = limit == null;
  const safeLimit = isUnlimited ? 1 : Math.max(1, limit);
  const progress = Math.min(100, Math.round((used / safeLimit) * 100));
  const formatResetAt = (value?: string | Date | null) => {
    if (!value) return null;
    const d = typeof value === 'string' ? new Date(value) : value;
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const resetText = formatResetAt(resetAt);
  const showCta = !isUnlimited && progress >= (thresholdPercent ?? 90);

  return (
    <Card sx={{ mb: 4, ...sx }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon && <Box sx={{ mr: 1 }}>{icon}</Box>}
          <Typography variant="h6">
            {title}: {used} / {isUnlimited ? '∞' : limit}
          </Typography>
        </Box>
        {!isUnlimited && (
          <>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ height: 10, borderRadius: 5, mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary">
              {used >= (limit || 0)
                ? `You've reached your ${title.toLowerCase()} limit.`
                : `You can add ${(limit || 0) - used} more.`}
            </Typography>
          </>
        )}
        {resetText && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Resets on {resetText}
          </Typography>
        )}
        {showCta && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
            {isAdmin ? (
              <Button
                variant="contained"
                color="primary"
                size="small"
                component={RouterLink}
                to={upgradeHref}
              >
                Upgrade plan
              </Button>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Near limit. Please contact your WaveScoop admin to upgrade.
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ResourceLimitCard;


